type ErrorLike = { name?: unknown; message?: unknown; stack?: unknown };

function isErrorLike(value: unknown): value is ErrorLike {
  return typeof value === 'object' && value !== null;
}

const STATIC_CHUNK_MARKERS = [
  '/_next/static/chunks/',
  'static/chunks/',
] as const;

/** Turbopack cascade when a dependency chunk failed to load or parse. */
const TURBOPACK_MODULE_FACTORY_MESSAGE = 'module factory is not available';

/**
 * SyntaxErrors at the start of a chunk file: CSS or HTML served where JS was
 * expected after a stale-client skew, or a truncated download the browser
 * cached zero-padded ("Invalid character: '\0'"). Deliberately narrow — a
 * generic parse message like "Unexpected EOF" also comes out of the `ses`
 * sandbox evaluating user scripts, and is left to the stack check below.
 */
const CHUNK_PARSE_SYNTAX_PATTERNS = [
  'Invalid character:',
  'Invalid or unexpected token',
] as const;

function isChunkLoadMessage(message: string): boolean {
  return (
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes(TURBOPACK_MODULE_FACTORY_MESSAGE)
  );
}

function referencesStaticChunk(error: ErrorLike): boolean {
  const stack = typeof error.stack === 'string' ? error.stack : '';
  return STATIC_CHUNK_MARKERS.some((marker) => stack.includes(marker));
}

function isChunkParseSyntaxError(error: ErrorLike): boolean {
  if (error.name !== 'SyntaxError') {
    return false;
  }

  const message = typeof error.message === 'string' ? error.message : '';

  if (referencesStaticChunk(error)) {
    return true;
  }

  return CHUNK_PARSE_SYNTAX_PATTERNS.some((pattern) =>
    message.includes(pattern),
  );
}

function isChunkLoadString(message: string): boolean {
  if (isChunkLoadMessage(message)) {
    return true;
  }

  if (!message.startsWith('SyntaxError:')) {
    return false;
  }

  return CHUNK_PARSE_SYNTAX_PATTERNS.some((pattern) =>
    message.includes(pattern),
  );
}

/**
 * Matches the error shapes Next.js/webpack/Turbopack throw when a JS chunk or a
 * dynamically imported module fails to load — typically a stale client whose
 * build no longer matches what the server is now serving.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (typeof error === 'string') {
    return isChunkLoadString(error);
  }

  if (!isErrorLike(error)) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  const message = typeof error.message === 'string' ? error.message : '';
  if (isChunkLoadMessage(message)) {
    return true;
  }

  if (isChunkParseSyntaxError(error)) {
    return true;
  }

  return false;
}
